/* global femcare, axios */

femcare.search.searchSuggest = {
    minSearchLength: 3,
    activeIndex: -1,
    requestIndex: 0,
    antiforgeryToken: '',

    /**
     * Initializes search suggestions for the search modal.
     */
    init() {
        this.searchModal = document.querySelector('.modal--search');
        if (!this.searchModal) {
            return;
        }

        this.form = this.searchModal.querySelector('.search-form');
        this.searchInput = this.form?.querySelector('input[type="text"]');
        this.suggestions = this.form?.querySelector('.search-suggest');
        this.suggestionsList = null;
        this.clearButton = this.form?.querySelector('.search-suggest__clear');
        this.trendingContent = this.searchModal.querySelector('.modal--search__trending');

        if (!this.form || !this.searchInput || !this.suggestions || !this.clearButton) {
            return;
        }

        this.setupAccessibility();
        this.bindEvents();
    },

    /**
     * Adds combobox attributes to the existing search modal input.
     */
    setupAccessibility() {
        this.searchInput.setAttribute('role', 'combobox');
        this.searchInput.setAttribute('aria-autocomplete', 'list');
        this.searchInput.setAttribute('aria-expanded', 'false');
    },

    /**
     * Binds input, click and keyboard events.
     */
    bindEvents() {
        const debouncedSearch = femcare.shared.util.debounce(() => {
            this.handleInput();
        }, 300);

        this.searchInput.addEventListener('input', () => {
            this.toggleClearButton();
            debouncedSearch();
        });

        this.searchInput.addEventListener('keydown', (event) => {
            this.handleKeydown(event);
        });

        this.clearButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.clearSearch(true);
        });

        document.addEventListener('click', (event) => {
            this.handleDocumentClick(event);
        });

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class' && !this.searchModal.classList.contains('displayed')) {
                    this.clearSearch(false);
                }
            });
        });

        observer.observe(this.searchModal, { attributes: true });
    },

    /**
     * Handles text input and starts autocomplete lookup after the minimum length.
     */
    handleInput() {
        const searchTerm = this.getSearchTerm();

        if (searchTerm.length < this.minSearchLength) {
            this.hideSuggestions();
            return;
        }

        this.fetchSuggestions(searchTerm);
    },

    /**
     * Fetches search suggestions from the backend.
     * @param {string} searchTerm
     */
    async fetchSuggestions(searchTerm) {
        const currentRequestIndex = ++this.requestIndex;
        const suggestUrl = femcare.shared.util.resolveProperApi(
            'ajax/core/customsearch/suggest',
            '/json/mock-api/customsearch-autocomplete.json'
        );
        const headers = {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
        };

        try {
            if (!femcare.shared.util.isLocalDevelopment()) {
                this.antiforgeryToken = this.antiforgeryToken || await femcare.form.protection.provideCSRFToken();
                headers['X-Request-Verification-Token'] = this.antiforgeryToken;
            }
        } catch {
            if (currentRequestIndex === this.requestIndex) {
                this.hideSuggestions();
            }

            return;
        }

        axios
            .get(suggestUrl, {
                params: { q: searchTerm },
                headers,
            })
            .then((response) => {
                if (currentRequestIndex !== this.requestIndex || searchTerm !== this.getSearchTerm()) {
                    return;
                }

                const responseData = response.data || {};
                if (!responseData.success) {
                    this.hideSuggestions();
                    return;
                }

                const suggestions = this.normalizeSuggestions(responseData);

                this.renderSuggestions(suggestions, searchTerm);
            })
            .catch(() => {
                if (currentRequestIndex === this.requestIndex) {
                    this.hideSuggestions();
                }
            });
    },

    /**
     * Normalizes search suggestion response keywords.
     * @param {object} responseData
     * @returns {Array}
     */
    normalizeSuggestions(responseData) {
        if (Array.isArray(responseData.content?.suggestions)) {
            return responseData.content.suggestions
                .filter((suggestion) => typeof suggestion === 'string')
                .map((suggestion) => suggestion.trim())
                .filter(Boolean);
        }

        if (Array.isArray(responseData.data)) {
            return responseData.data
                .map((item) => item && item.keyword)
                .filter((keyword) => typeof keyword === 'string')
                .map((keyword) => keyword.trim())
                .filter(Boolean);
        }

        return [];
    },

    /**
     * Renders autocomplete suggestions.
     * @param {Array} suggestions
     * @param {string} searchTerm
     */
    renderSuggestions(suggestions, searchTerm) {
        this.removeSuggestionsList();
        this.activeIndex = -1;
        this.searchInput.removeAttribute('aria-activedescendant');

        if (!suggestions.length) {
            this.hideSuggestions();
            return;
        }

        this.createSuggestionsList();

        suggestions.forEach((keyword, index) => {
            const item = document.createElement('li');
            const link = document.createElement('a');
            const searchUrl = this.getSuggestionUrl(keyword);

            item.setAttribute('role', 'presentation');

            link.className = 'search-suggest__option';
            link.id = `searchSuggestOption-${index}`;
            link.href = searchUrl;
            link.setAttribute('role', 'option');
            link.setAttribute('aria-selected', 'false');
            link.setAttribute('data-keyword', keyword);

            this.appendHighlightedText(link, keyword, searchTerm);

            link.addEventListener('click', (event) => {
                event.preventDefault();
                this.selectSuggestion(keyword, index);
            });

            item.appendChild(link);
            this.suggestionsList.appendChild(item);
        });

        this.showSuggestions();
    },

    /**
     * Appends highlighted suggestion text without injecting HTML.
     * @param {HTMLElement} element
     * @param {string} keyword
     * @param {string} searchTerm
     */
    appendHighlightedText(element, keyword, searchTerm) {
        const lowerKeyword = keyword.toLowerCase();
        const lowerSearchTerm = searchTerm.toLowerCase();
        let currentIndex = 0;
        let matchIndex = lowerKeyword.indexOf(lowerSearchTerm);

        while (matchIndex !== -1) {
            if (matchIndex > currentIndex) {
                element.appendChild(document.createTextNode(keyword.slice(currentIndex, matchIndex)));
            }

            const match = document.createElement('span');
            match.className = 'search-suggest__match';
            match.textContent = keyword.slice(matchIndex, matchIndex + searchTerm.length);
            element.appendChild(match);

            currentIndex = matchIndex + searchTerm.length;
            matchIndex = lowerKeyword.indexOf(lowerSearchTerm, currentIndex);
        }

        if (currentIndex < keyword.length) {
            element.appendChild(document.createTextNode(keyword.slice(currentIndex)));
        }
    },

    /**
     * Handles arrow navigation, Enter and Escape on the combobox.
     * @param {KeyboardEvent} event
     */
    handleKeydown(event) {
        const options = this.getSuggestionOptions();
        const isSuggestionsOpen = !this.suggestions.classList.contains('hidden');

        if (event.key === 'Escape' && isSuggestionsOpen) {
            event.preventDefault();
            event.stopPropagation();
            this.hideSuggestions();
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();

            if (isSuggestionsOpen && this.activeIndex >= 0) {
                this.selectSuggestion(options[this.activeIndex].dataset.keyword, this.activeIndex);
            } else {
                this.hideSuggestions();
                this.submitSearch();
            }
            return;
        }

        if (!isSuggestionsOpen || !options.length) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.setActiveOption(this.activeIndex + 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.setActiveOption(this.activeIndex - 1);
        }
    },

    submitSearch() {
        if (this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        this.form.requestSubmit();
    },

    /**
     * Sets active option for keyboard users.
     * @param {number} nextIndex
     */
    setActiveOption(nextIndex) {
        const options = this.getSuggestionOptions();
        if (!options.length) {
            return;
        }

        const normalizedIndex = (nextIndex + options.length) % options.length;
        options.forEach((option, index) => {
            const isActive = index === normalizedIndex;
            option.classList.toggle('search-suggest__option--active', isActive);
            option.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.activeIndex = normalizedIndex;
        this.searchInput.setAttribute('aria-activedescendant', options[normalizedIndex].id);
    },

    /**
     * Navigates to the search page for the selected suggestion.
     * @param {string} keyword
     * @param {number} index
     */
    selectSuggestion(keyword, index) {
        const searchUrl = this.getSuggestionUrl(keyword);
        this.trackSuggestionClick(keyword, index, searchUrl);
        window.location.href = searchUrl;
    },

    /**
     * Builds search page URL for a suggestion.
     * @param {string} keyword
     * @returns {string}
     */
    getSuggestionUrl(keyword) {
        const searchUrl = new URL(this.form.action, window.location.origin);
        searchUrl.searchParams.set('q', keyword);

        return searchUrl.href;
    },

    /**
     * Tracks autocomplete suggestion clicks.
     * @param {string} keyword
     * @param {number} index
     * @param {string} url
     */
    trackSuggestionClick(keyword, index, url) {
        femcare.tracking.analytics.pushEvent('ga4_select_search_suggestion', {
            search_term: this.getSearchTerm(),
            suggestion_term: keyword,
            link_url: url,
            link_text: keyword,
            index: index + 1
        });
    },

    /**
     * Clears input, hides suggestions and optionally keeps focus in the field.
     * @param {boolean} shouldFocus
     */
    clearSearch(shouldFocus) {
        this.searchInput.value = '';
        this.toggleClearButton();
        this.hideSuggestions();
        this.requestIndex++;

        if (shouldFocus) {
            this.searchInput.focus();
        } else {
            this.searchInput.blur();
        }
    },

    /**
     * Handles modal/outside clicks according to desktop and modal-search rules.
     * @param {MouseEvent} event
     */
    handleDocumentClick(event) {
        if (!this.searchModal.classList.contains('displayed')) {
            return;
        }

        const clickedInsideInput = this.searchInput.contains(event.target);
        const clickedInsideClear = this.clearButton.contains(event.target);
        const clickedInsideSuggestions = this.suggestions.contains(event.target);

        if (clickedInsideInput || clickedInsideClear || clickedInsideSuggestions) {
            return;
        }

        const modalDialog = this.searchModal.querySelector('.modal__dialog');
        if (modalDialog && modalDialog.contains(event.target)) {
            this.clearSearch(false);
            return;
        }

        if (window.innerWidth >= 768 && femcare.modal) {
            femcare.modal.closeModal(this.searchModal);
        }
    },

    /**
     * Shows suggestions and fades modal content behind it.
     */
    showSuggestions() {
        this.suggestions.classList.remove('hidden');
        this.trendingContent?.classList.add('modal--search__trending--faded');
        this.searchInput.setAttribute('aria-expanded', 'true');
    },

    /**
     * Hides suggestions and restores modal content.
     */
    hideSuggestions() {
        this.suggestions.classList.add('hidden');
        this.trendingContent?.classList.remove('modal--search__trending--faded');
        this.searchInput.setAttribute('aria-expanded', 'false');
        this.searchInput.removeAttribute('aria-controls');
        this.searchInput.removeAttribute('aria-activedescendant');
        this.activeIndex = -1;
        this.removeSuggestionsList();
    },

    /**
     * Creates the listbox only when there are suggestions.
     */
    createSuggestionsList() {
        this.suggestionsList = document.createElement('ul');
        this.suggestionsList.className = 'search-suggest__list';
        this.suggestionsList.id = 'searchSuggestList';
        this.suggestionsList.setAttribute('role', 'listbox');
        this.suggestionsList.setAttribute(
            'aria-label',
            this.suggestions.getAttribute('data-label-suggestions') || 'Search suggestions'
        );
        this.suggestions.appendChild(this.suggestionsList);
        this.searchInput.setAttribute('aria-controls', this.suggestionsList.id);
    },

    /**
     * Removes the listbox to avoid an empty list in the DOM.
     */
    removeSuggestionsList() {
        if (this.suggestionsList) {
            this.suggestionsList.remove();
            this.suggestionsList = null;
        }
    },

    /**
     * Shows or hides the input clear button.
     */
    toggleClearButton() {
        this.clearButton.classList.toggle('hidden', !this.searchInput.value.length);
    },

    /**
     * Returns rendered suggestion option elements.
     * @returns {Array}
     */
    getSuggestionOptions() {
        if (!this.suggestionsList) {
            return [];
        }

        return Array.from(this.suggestionsList.querySelectorAll('.search-suggest__option'));
    },

    /**
     * Returns trimmed search input value.
     * @returns {string}
     */
    getSearchTerm() {
        return this.searchInput.value.trim();
    },
};
